import { Navigate, RouteObject } from "react-router-dom";
import { Config } from "@/components/pages";
import { RequireAccess } from "./RequireAccess/RequireAccess";
import { ContractPricingProfilesRoute, CustomRatesRoute, PricingProfileRoute, StateRatesRoute, ZoneRatesRoute } from "@/components/organisms";

const businessRoutes: RouteObject = {
  path: "business",
  handle: { pageName: "Business Settings" },
  children: [
    {
      index: true,
      element: <Config.Business />,
    },
    {
      path: ":pageSlug?",
      element: <RequireAccess action="manage" subject="providerConfigProfileAdvanced" />,
      children: [
        {
          path: "access-control",
          handle: { pageName: "Access Control" },
          children: [
            {
              index: true,
              element: <Config.Business.AccessControl />,
            },
            {
              path: "edit",
              element: <Config.Business.AccessControl.Edit />,
            },
          ],
        },
      ],
    },
    {
      path: "business-details",
      handle: { pageName: "Business Details" },
      children: [
        {
          index: true,
          element: <Config.Business.BusinessDetails />,
        },
        {
          path: "edit",
          element: <Config.Business.BusinessDetails.Edit />,
        },
      ],
    },
    {
      path: "website-details",
      handle: { pageName: "Website Details" },
      children: [
        {
          index: true,
          element: <Config.Business.WebsiteDetails />,
        },
        {
          path: "edit",
          element: <Config.Business.WebsiteDetails.Edit />,
        },
      ],
    },
    {
      path: "email-notifications",
      handle: { pageName: "Email Notifications" },
      children: [
        {
          index: true,
          element: <Config.Business.EmailNotifications />,
        },
        {
          path: "edit",
          element: <Config.Business.EmailNotifications.Edit />,
        },
      ],
    },
    {
      path: "sms-voice-notifications",
      handle: { pageName: "SMS / Voice Notifications" },
      children: [
        {
          index: true,
          element: <Config.Business.SMSVoiceNotifications />,
        },
        {
          path: "edit",
          element: <Config.Business.SMSVoiceNotifications.Edit />,
        },
      ],
    },
    {
      path: "display-options",
      handle: { pageName: "Display Options" },
      children: [
        {
          index: true,
          element: <Config.Business.DisplayOptions />,
        },
        {
          path: "edit",
          element: <Config.Business.DisplayOptions.Edit />,
        },
      ],
    },
  ],
};

const tripRoutes: RouteObject = {
  path: "trip",
  handle: { pageName: "Trip Settings" },
  children: [
    {
      index: true,
      element: <Config.Trip />,
    },
    {
      path: ":pageSlug?",
      element: <RequireAccess action="manage" subject="providerConfigPricing" />,
      children: [
        {
          path: "trip-cost",
          handle: { pageName: "Trip Cost" },
          children: [
            {
              index: true,
              element: <Config.Trip.TripPanel.TripCostV2 />,
            },
            {
              path: "default-rates",
              handle: { pageName: "Default Rates" },
              element: <Config.Trip.TripPanel.TripCostV2.DefaultRates />,
            },
            {
              path: "custom-rates",
              handle: { pageName: "Custom Rates" },
              element: <CustomRatesRoute />,
              children: [
                {
                  index: true,
                  element: <Config.Trip.TripPanel.TripCostV2.CustomRates />,
                },
                {
                  path: ":slug",
                  handle: { pageKey: "stateName" },
                  children: [
                    {
                      index: true,
                      element: <Config.Trip.TripPanel.TripCostV2.CustomRates.StateRates />,
                    },
                    {
                      path: "edit",
                      element: <Config.Trip.TripPanel.TripCostV2.CustomRates.StateRatesEdit />,
                    },
                  ],
                },
              ],
            },
            {
              path: "contract-pricing-profiles",
              handle: { pageName: "Contract Pricing Profiles" },
              element: <ContractPricingProfilesRoute />,
              children: [
                {
                  index: true,
                  element: <Config.Trip.TripPanel.TripCostV2.ContractPricingProfiles />,
                },
                {
                  path: "add",
                  element: <Config.Trip.TripPanel.TripCostV2.ContractPricingProfiles.Add />,
                },
                {
                  path: ":id",
                  handle: {
                    pageKey: "profileName",
                    pageName: "Pricing Profile",
                  },
                  element: <PricingProfileRoute />,
                  children: [
                    {
                      index: true,
                      element: <Config.Trip.TripPanel.TripCostV2.ContractPricingProfiles.PricingProfile />,
                    },
                    {
                      path: "state-rates",
                      handle: { pageName: "State Rates" },
                      element: <StateRatesRoute />,
                      children: [
                        {
                          index: true,
                          element: <Config.Trip.TripPanel.TripCostV2.ContractPricingProfiles.PricingProfile.StateRates />,
                        },
                        {
                          path: ":slug",
                          handle: { pageKey: "stateName" },
                          children: [
                            {
                              index: true,
                              element: <Config.Trip.TripPanel.TripCostV2.ContractPricingProfiles.PricingProfile.StateRates.View />,
                            },
                            {
                              path: "edit",
                              element: <Config.Trip.TripPanel.TripCostV2.ContractPricingProfiles.PricingProfile.StateRates.Edit />,
                            },
                          ],
                        },
                      ],
                    },
                    {
                      path: "zone-rates",
                      handle: { pageName: "Zone Rates" },
                      element: <ZoneRatesRoute />,
                      children: [
                        {
                          index: true,
                          element: <Config.Trip.TripPanel.TripCostV2.ContractPricingProfiles.PricingProfile.ZoneRates />,
                        },
                        {
                          path: "add",
                          element: <Config.Trip.TripPanel.TripCostV2.ContractPricingProfiles.PricingProfile.ZoneRates.Add />,
                        },
                        {
                          path: ":slug",
                          handle: { pageKey: "zoneName" },
                          element: <Config.Trip.TripPanel.TripCostV2.ContractPricingProfiles.PricingProfile.ZoneRates.Edit />,
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: "time-and-day",
          handle: { pageName: "Time & Day (Multiplier)" },
          children: [
            {
              index: true,
              element: <Config.Trip.TripPanel.TimeAndDay />,
            },
            {
              path: "edit",
              children: [{ index: true, element: <Config.Trip.TripPanel.TimeAndDay.Edit /> }],
            },
          ],
        },

        {
          path: "cancellation-fees",
          handle: { pageName: "Cancellation Fees" },
          children: [
            {
              index: true,
              element: <Config.Trip.TripPanel.CancellationFees />,
            },
            {
              path: ":mode?/:type?",
              handle: { pageName: "Cancellation Fees" },
              element: <Config.Trip.TripPanel.CancellationFees />,
            },
          ],
        },
        {
          path: "unscheduled-stops",
          handle: { pageName: "Unscheduled Stops" },
          children: [
            {
              index: true,
              element: <Config.Trip.TripPanel.UnscheduledStops />,
            },
            {
              path: "edit",
              element: <Config.Trip.TripPanel.UnscheduledStops.Edit />,
            },
          ],
        },
        {
          path: "hourly-daily-hire",
          handle: { pageName: "Hourly - Daily Hire" },
          children: [
            {
              path: "*",
              element: <Config.Trip.TripPanel.HourlyDailyHire />,
              children: [
                {
                  index: true,
                  element: <Navigate to="hourly" replace />,
                },
                {
                  path: "hourly",
                  children: [
                    {
                      index: true,
                      element: <Config.Trip.TripPanel.HourlyDailyHire.Hourly />,
                    },
                    {
                      path: ":mode?/:type?",
                      element: <Config.Trip.TripPanel.HourlyDailyHire.Hourly />,
                    },
                  ],
                },
                {
                  path: "daily",
                  children: [
                    {
                      index: true,
                      element: <Config.Trip.TripPanel.HourlyDailyHire.Daily />,
                    },
                    {
                      path: ":mode?/:type?",
                      element: <Config.Trip.TripPanel.HourlyDailyHire.Daily />,
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: "vehicle-categories",
      handle: { pageName: "Transfer & Vehicle Options" },
      element: <RequireAccess action="manage" subject="providerConfigVehicle" />,
      children: [
        {
          index: true,
          element: <Config.Trip.TripPanel.VehicleCategories />,
        },
        {
          path: ":vehicleCategoryId",
          handle: { pageName: "Edit Vehicle Category" },
          element: <Config.Trip.TripPanel.VehicleCategories.Edit />,
        },
        {
          path: "add",
          handle: { pageName: "Add Vehicle Category" },
          element: <Config.Trip.TripPanel.VehicleCategories.Add />,
        },
      ],
    },
    {
      path: "transfer-types",
      handle: { pageName: "Transfer & Vehicle Options" },
      element: <RequireAccess action="manage" subject="providerConfigVehicle" />,
      children: [
        {
          index: true,
          element: <Config.Trip.TripPanel.TransferTypes />,
        },
        {
          path: ":transferTypeId",
          handle: { pageName: "Edit Transfer Type" },
          element: <Config.Trip.TripPanel.TransferTypes.Edit />,
        },
        {
          path: "add",
          handle: { pageName: "Add Transfer Type" },
          element: <Config.Trip.TripPanel.TransferTypes.Add />,
        },
      ],
    },
    {
      path: "fixed-cost-extras",
      handle: { pageName: "Transfer & Vehicle Options" },
      element: <Config.Trip.TripPanel.FixedCostExtras />,
      children: [
        {
          path: "add",
          element: <Config.Trip.TripPanel.FixedCostExtras.Add />,
        },
        {
          path: ":fixedCostExtraId",
          element: <Config.Trip.TripPanel.FixedCostExtras.Edit />,
        },
      ],
    },
    {
      path: "billing-settings",
      handle: { pageName: "Billing Settings" },
      element: <RequireAccess action="manage" subject="providerConfigProfileAdvanced" />,
      children: [
        {
          index: true,
          element: <Config.Trip.TripPanel.BillingSettings />,
        },
        {
          path: "edit",
          element: <Config.Trip.TripPanel.BillingSettings.Edit />,
        },
      ],
    },
    {
      path: "waiting-times",
      handle: { pageName: "Waiting Times" },
      element: <RequireAccess action="manage" subject="providerConfigProfileAdvanced" />,
      children: [
        {
          index: true,
          element: <Config.Trip.TripPanel.WaitingTimes />,
        },
        {
          path: "edit",
          element: <Config.Trip.TripPanel.WaitingTimes.Edit />,
        },
      ],
    },
    {
      path: "dispatch-settings",
      handle: { pageName: "Dispatch Settings" },
      element: <RequireAccess action="manage" subject="providerConfigProfileAdvanced" />,
      children: [
        {
          index: true,
          element: <Config.Trip.DispatchPanel.DispatchSettings />,
        },
        {
          path: ":mode?/:type?",
          element: <Config.Trip.DispatchPanel.DispatchSettings />,
        },
      ],
    },
  ],
};

const documentRoutes: RouteObject = {
  path: "documents",
  handle: { pageName: "Documents Links" },
  children: [
    {
      index: true,
      element: <Config.Documents />,
    },
    {
      path: "terms-and-conditions",
      handle: { pageName: "Terms and Conditions" },
      children: [
        {
          index: true,
          element: <Config.Documents.TermsAndConditions />,
        },
        {
          path: ":id",
          element: <Config.Documents.TermsAndConditions.History />,
        },
      ],
    },
  ],
};

const marketingRoutes: RouteObject = {
  path: "marketing",
  handle: { pageName: "Marketing Tools" },
  children: [
    {
      index: true,
      element: <Config.Marketing />,
    },
    {
      path: "qr-templates",
      handle: { pageName: "Contact Printer Supplier" },
      children: [
        {
          index: true,
          element: <Config.Marketing.ClientTools.SendToPrinter />,
        },
      ],
    },
  ],
};

const userAccountsRoutes: RouteObject = {
  path: "user-accounts",
  element: <RequireAccess action="manage" subject="providerAccountDirectory" />,
  handle: { pageName: "User Accounts" },
  children: [
    {
      index: true,
      element: <Config.UserAccounts />,
    },
    {
      path: ":id/history",
      handle: { pageName: "History Logs" },
      element: <Config.UserAccounts.History />,
    },
  ],
};

export const configRoutes: RouteObject = {
  path: "config",
  element: <RequireAccess action="view" subject="providerConfig" />,
  handle: { pageName: "Settings" },
  children: [
    {
      path: "*",
      element: <Config />,
      children: [
        {
          index: true,
          element: <Navigate to="business" replace />,
        },
        businessRoutes,
        tripRoutes,
        marketingRoutes,
        documentRoutes,
        userAccountsRoutes,
      ],
    },
  ],
};
